import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { projectsAPI, tasksAPI } from '../services/api';
import { LifecycleState, lifecycleBusy } from '../lib/billing';
import { AuthContext } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

interface Project {
  id: string;
  name: string;
  description: string;
  githubRepoName: string;
  githubOwner: string;
  githubUrl: string;
  defaultBranch: string;
  isPrivate: boolean;
  language?: string;
  status: string;
  stats: {
    totalTasks: number;
    completedTasks: number;
    totalPRs: number;
    mergedPRs: number;
  };
  createdAt: string;
  lastSync?: string;
  workspaceDirty?: boolean;
  lastUnsavedTaskId?: string | null;
  lastUnsavedAt?: string | null;
  pendingWorkspaceAction?: 'discard' | null;
}

interface Branch {
  name: string;
  protected: boolean;
  commit: {
    sha: string;
    url: string;
  };
}

export interface IProject {
  id: string;
  name: string;
  description: string;
  githubUrl: string;
  githubRepoName: string;
  githubOwner: string;
  defaultBranch: string;
  baseUrl?: string; // Amplify base URL for main branch deployment
  thumbnailUrl?: string | null; // server-captured app screenshot for the dashboard card
  thumbnailUpdatedAt?: string | null; // drives the ?v= cache-buster on the thumbnail img
  /** The published address, once there is one (#353). */
  prodUrl?: string | null;
  /** The editable label half of the address, e.g. "joes-plumbing". */
  subdomain?: string | null;
  /** The address to SHOW: live once published, otherwise the prospective one the
   *  server derives. Typed here rather than read through an `as any` cast — the cast
   *  is what hid getSummary never sending prodUrl at all, so the rename dialog's
   *  address line silently never rendered. */
  webAddress?: string | null;
  /** False means webAddress is a preview, not a reservation, so the copy must not
   *  promise it (an unpublished label can still gain a -2 suffix on collision). */
  webAddressIsLive?: boolean;
  settings?: {
    deployment?: {
      type?: 'none' | 'amplify' | 'github_actions';
      workflowFile?: string;
      timeoutMinutes?: number;
    };
  };
  isPrivate: boolean;
  language?: string;
  status: string;
  stats: {
    totalTasks: number;
    completedTasks: number;
    totalPRs: number;
    mergedPRs: number;
  };
  createdAt: string;
  lastSync?: string;
  /** App owner's user id (from getSummary). Lets the client tell apps you OWN
   *  apart from apps shared WITH you (drives the "Shared by …" badge). */
  owner?: string;
  /** Small collaborator preview (owner + AppAccess grantees, up to 4) attached
   *  by GET /api/projects. Drives the avatar stack on the Dashboard/Apps cards. */
  collaborators?: { userId: string; name?: string | null; avatar?: string | null; isOwner?: boolean }[];
  // Workspace state surfaced from the server's project summary. Used by the
  // dashboard's new-task flow to decide whether to show the unsaved-changes
  // modal before creating a new task.
  workspaceDirty?: boolean;
  lastUnsavedTaskId?: string | null;
  lastUnsavedAt?: string | null;
  pendingWorkspaceAction?: 'discard' | null;
  /** Set when the user pins this app (dashboard card). Truthy = pinned.
   *  Persisted server-side via PUT /projects/:id { pinned }. */
  pinnedAt?: string | null;
  /** Scratch-flow provenance from getSummary. The task page's kind-aware copy
   *  (TaskDetailPage → resolveAppKind) reads these; the home Recent grid does
   *  NOT — it is one cross-surface list since 2026-09. */
  source?: 'imported' | 'scratch';
  buildKind?: 'landing' | 'fullstack' | 'mobile' | 'video' | null;
  /** Paused / archived by the pricing lifecycle or the owner; null when live. */
  lifecycle?: { state: LifecycleState; pauseReason?: string | null; archiveReason?: string | null; pausedAt?: string | null; archivedAt?: string | null } | null;
  /** The phone app's folder inside the repo; null = the repo root. */
  mobileAppDir?: string | null;
  /** Set when the project came through the import check: how many apps the
   *  repo holds, and whether its phone app is not the one shown. */
  importCheck?: { appCount: number; phoneAppHidden: boolean } | null;
  /** Registry slug when deployed from a template. Gates the pre-save
   *  self-check turn (presaveSkip) — not a Recents/shelf filter any more. */
  templateId?: string | null;
}

export interface IBranch {
  name: string;
  commit: {
    sha: string;
    url: string;
  };
}

interface ITask {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  prNumber?: number;
  prUrl?: string;
  prStatus?: string;
  startedAt?: string;
  completedAt?: string;
  executionTime?: number;
  createdAt: string;
}

interface IProjectContext {
  projects: IProject[];
  selectedProject: IProject | null;
  branches: IBranch[];
  projectTasks: { [projectId: string]: ITask[] };
  loading: boolean;
  // True once fetchProjects has succeeded at least once. `loading` starts
  // false and is shared across all project ops, so "projects === [] and not
  // loading" is also true BEFORE the first fetch — gate zero-state UI on
  // this flag instead.
  hasLoadedProjects: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  fetchProjectById: (id: string) => Promise<void>;
  // Clears selectedProject. Used by the dashboard composer's "Create new app"
  // entry to flip into new-app mode (selectedProject === null).
  clearSelectedProject: () => void;
  fetchBranchesForProject: (id: string) => Promise<void>;
  fetchProjectTasks: (projectId: string) => Promise<void>;
  createProject: (data: {
    repoUrl: string;
    name?: string;
    description?: string;
    branch?: string;
    baseUrl?: string;
    deploymentType?: 'none' | 'amplify' | 'github_actions';
    deploymentWorkflowFile?: string;
    deploymentTimeoutMinutes?: number;
    environmentVariables?: Array<{ key: string; value: string; isSecret: boolean }>;
    mobileAppDir?: string | null;
  }) => Promise<IProject>;
  updateProject: (id: string, data: any) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  /** The owner archives / restores an app. Rejects with the raw API error (callers word it). */
  archiveProject: (id: string) => Promise<void>;
  restoreProject: (id: string) => Promise<void>;
  syncProject: (id: string) => Promise<void>;
}

export const ProjectContext = createContext<IProjectContext | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

/** How often a queued archive / restore is polled, and for how long (the job's own 2h timeout). */
const LIFECYCLE_POLL_MS = 5000;
const LIFECYCLE_POLL_MAX = (2 * 60 * 60 * 1000) / LIFECYCLE_POLL_MS;

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const { token, user } = useContext(AuthContext);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const [branches, setBranches] = useState<IBranch[]>([]);
  const [projectTasks, setProjectTasks] = useState<{ [projectId: string]: ITask[] }>({});
  const [loading, setLoading] = useState(false);
  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same socket instance TaskContext uses — ProjectProvider mounts inside
  // WebSocketProvider (App.tsx), so this never opens a second connection.
  const { socket } = useWebSocket();

  const fetchProjects = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await projectsAPI.getAll();
      // A 200 whose body is not the API's (a proxy or captive-portal page, an
      // extension) left `projects` undefined and crashed the Dashboard on
      // projects.length (Sentry NODE-EXPRESS-15).
      if (!Array.isArray(response.data?.projects)) {
        setError('Failed to fetch projects');
        return;
      }
      setProjects(response.data.projects);
      // Clear any stale error from a prior op (e.g. a 403 from opening a shared
      // app you can't admin, or a deleted-app 404). `error` is shared across all
      // project ops, and the Apps page re-toasts it as "Failed to fetch
      // projects" — so a successful list MUST reset it, or an unrelated old
      // error keeps surfacing on a page whose own fetch actually succeeded.
      setError(null);
      setHasLoadedProjects(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchProjectById = useCallback(async (id: string) => {
    setLoading(true);
    // Clear up front so `error` only carries the result of THIS lookup. The
    // App Details page relies on that to tell "deleted / no access" (error set)
    // apart from "still loading" (error null) without a false positive from a
    // stale error left by an earlier op.
    setError(null);
    try {
      const response = await projectsAPI.getById(id);
      setSelectedProject(response.data.project);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranchesForProject = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const response = await projectsAPI.getBranches(id);
      setBranches(response.data.branches);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectTasks = useCallback(async (projectId: string) => {
    try {
      const response = await tasksAPI.getByProject(projectId, { limit: 10 });
      setProjectTasks(prev => ({
        ...prev,
        [projectId]: response.data.tasks
      }));
    } catch (err: any) {
      console.error('Failed to fetch project tasks:', err);
    }
  }, []);

  const createProject = async (data: {
    repoUrl: string;
    name?: string;
    description?: string;
    branch?: string;
    baseUrl?: string;
    deploymentType?: 'none' | 'amplify' | 'github_actions';
    deploymentWorkflowFile?: string;
    deploymentTimeoutMinutes?: number;
    environmentVariables?: Array<{ key: string; value: string; isSecret: boolean }>;
    mobileAppDir?: string | null;
  }) => {
    setLoading(true);
    try {
      const response = await projectsAPI.create(data);
      const createdProject = response.data.project;
      
      await fetchProjects(); // Refresh projects list
      
      // Auto-select the newly created project
      setSelectedProject(createdProject);
      
      return createdProject;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create project');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (id: string, data: any) => {
    try {
      setError(null);
      const response = await projectsAPI.update(id, data);
      // MERGE, don't replace. PUT returns getSummary(), which lacks the
      // list-only fields GET /api/projects attaches (webAddress,
      // webAddressIsLive, collaborators). Replacing the row wholesale used to
      // strip the address line from the next Rename dialog and drop the
      // avatar stack after every rename/pin until the next refetch.
      const patch: Partial<IProject> = response.data.project || {};
      const merge = (p: IProject): IProject => ({ ...p, ...patch });
      setProjects(prev => prev.map(p => (p.id === id ? merge(p) : p)));
      setSelectedProject(prev => (prev && prev.id === id ? merge(prev) : prev));
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update project');
      throw error;
    }
  };

  const setLifecycle = (id: string, lifecycle: IProject['lifecycle']) => {
    const live = !lifecycle || lifecycle.state === 'live' ? null : lifecycle;
    setProjects(prev => prev.map(p => (p.id === id ? { ...p, lifecycle: live } : p)));
    setSelectedProject(prev => (prev && prev.id === id ? { ...prev, lifecycle: live } : prev));
  };

  // A 202 from archive / restore means the work runs in the lifecycle queue
  // (review of PR #890, D3: the tar or the untar can take a while and never
  // rides the request). The row reads 'archiving' / 'restoring' until the
  // worker is done; poll it so the card's tag and the banner follow, for at
  // most the job's own timeout. One poll per app; cleared on unmount.
  const lifecyclePolls = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => () => { Object.values(lifecyclePolls.current).forEach(clearTimeout); }, []);
  const pollLifecycle = (id: string, attempt = 0) => {
    if (lifecyclePolls.current[id] || attempt >= LIFECYCLE_POLL_MAX) return;
    lifecyclePolls.current[id] = setTimeout(async () => {
      delete lifecyclePolls.current[id];
      let lc: IProject['lifecycle'] = null;
      try {
        const r = await projectsAPI.getById(id);
        lc = (r.data.project && r.data.project.lifecycle) || null;
      } catch {
        pollLifecycle(id, attempt + 1);
        return;
      }
      setLifecycle(id, lc);
      if (lc && lifecycleBusy(lc.state)) pollLifecycle(id, attempt + 1);
    }, LIFECYCLE_POLL_MS);
  };

  const archiveProject = async (id: string) => {
    const r = await projectsAPI.archive(id);
    const queued = r.status === 202 || !!r.data.queued;
    setLifecycle(id, r.data.lifecycle || { state: queued ? 'archiving' : 'archived' });
    if (queued) pollLifecycle(id);
  };

  const restoreProject = async (id: string) => {
    const r = await projectsAPI.restore(id);
    const queued = r.status === 202 || !!r.data.queued;
    setLifecycle(id, r.data.lifecycle || (queued ? { state: 'restoring' } : null));
    if (queued) pollLifecycle(id);
  };

  const deleteProject = async (id: string) => {
    try {
      setError(null);
      await projectsAPI.delete(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      if (selectedProject?.id === id) {
        setSelectedProject(null);
      }
    } catch (error: any) {
      // The server finishes a delete even when the request does not: a timeout
      // or dropped connection (no response) can hide a delete that succeeded,
      // and a retry of one finds the app already gone (404). Ask the server
      // which it was instead of reporting a failure for a deleted app.
      let gone = error?.response?.status === 404;
      if (!gone && !error?.response) {
        try {
          const response = await projectsAPI.getAll();
          const list = Array.isArray(response.data?.projects) ? response.data.projects : null;
          gone = !!list && !list.some((p: IProject) => p.id === id);
          if (list) setProjects(list);
        } catch (_e) { /* cannot tell: report the original failure */ }
      }
      if (gone) {
        setProjects(prev => prev.filter(p => p.id !== id));
        if (selectedProject?.id === id) setSelectedProject(null);
        return;
      }
      setError(error.response?.data?.error || 'Failed to delete project');
      throw error;
    }
  };

  const syncProject = async (id: string) => {
    try {
      setError(null);
      await projectsAPI.sync(id);
      await fetchProjects(); // Refresh projects to get updated status
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to sync project');
      throw error;
    }
  };

  // Only load projects once the user is past the waitlist gate — authed,
  // onboarded, and allowed in. Fetching earlier hits the gated /api/projects,
  // which 403s for a not-yet-allowed user; the global response interceptor
  // would then HARD-redirect (window.location) to /waitlist before onboarding
  // or ProtectedRoute can route them properly — so a fresh signup would skip
  // onboarding and land on the wall. Mirror ProtectedRoute's predicate:
  // a missing `allowed` is treated as allowed.
  useEffect(() => {
    if (user && user.onboardingCompleted && user.allowed !== false) {
      fetchProjects();
    }
  }, [user?.onboardingCompleted, user?.allowed, fetchProjects]);

  // Live rename. PUT /api/projects/:id broadcasts `project_renamed`
  // {projectId, name} to user_<id> for the owner and every collaborator, so a
  // rename from ANY surface — a card kebab, the workspace top bar, the app
  // details page, a teammate's tab — lands on every grid here without a
  // refetch. Idempotent on purpose: the tab that issued the rename already
  // applied it via updateProject, so its own echo returns `prev` untouched
  // and React skips the re-render. TaskContext keeps its own handler for the
  // open workspace's copy of the name. Keyed on the `socket` state (not the
  // ref-based subscribe/unsubscribe, which no-op before the socket exists).
  useEffect(() => {
    if (!socket) return;
    const onProjectRenamed = (data: { projectId?: string; name?: string }) => {
      const projectId = data && data.projectId ? String(data.projectId) : '';
      const name = data && typeof data.name === 'string' ? data.name : '';
      if (!projectId || !name) return;
      setProjects(prev => {
        const i = prev.findIndex(p => String(p.id) === projectId);
        if (i < 0 || prev[i].name === name) return prev;
        const next = prev.slice();
        next[i] = { ...prev[i], name };
        return next;
      });
      setSelectedProject(prev =>
        prev && String(prev.id) === projectId && prev.name !== name ? { ...prev, name } : prev
      );
    };
    socket.on('project_renamed', onProjectRenamed);
    return () => { socket.off('project_renamed', onProjectRenamed); };
  }, [socket]);

  // Multiplayer/team-invites live updates: access can change from another
  // surface (a teammate switching a member to 'all' mode, granting/revoking a
  // single app, or an admin removing this user entirely) while the Apps list
  // is open here. Refetch so the grid never drifts stale, and drop the
  // selected project out from under an open workspace the instant this
  // user's access to it is revoked.
  useEffect(() => {
    if (!socket) return;
    const onAccessGranted = () => { fetchProjects(); };
    const onAccessChanged = () => { fetchProjects(); };
    const onAccessRevoked = (d: { projectId?: string }) => {
      fetchProjects();
      setSelectedProject(prev => (prev && String(prev.id) === String(d?.projectId) ? null : prev));
    };
    socket.on('app_access_granted', onAccessGranted);
    socket.on('app_access_changed', onAccessChanged);
    socket.on('access_revoked', onAccessRevoked);
    return () => {
      socket.off('app_access_granted', onAccessGranted);
      socket.off('app_access_changed', onAccessChanged);
      socket.off('access_revoked', onAccessRevoked);
    };
  }, [socket, fetchProjects]);

  const clearSelectedProject = () => {
    setSelectedProject(null);
    setBranches([]);
  };

  const value = {
    projects,
    selectedProject,
    branches,
    projectTasks,
    loading,
    hasLoadedProjects,
    error,
    fetchProjects,
    fetchProjectById,
    clearSelectedProject,
    fetchBranchesForProject,
    fetchProjectTasks,
    createProject,
    updateProject,
    deleteProject,
    archiveProject,
    restoreProject,
    syncProject,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}; 